/**
 * InterviewGuestManager Component
 * 
 * Section for managing external guest interviewers (always open, no accordion)
 */

import React, { useState } from 'react';
import { Input, Button, List, Typography, Flex, message } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface InterviewGuestManagerProps {
  value?: string[];
  onChange?: (guests: string[]) => void;
}

const EMAIL_REGEX = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
const MAX_GUESTS = 5;

const InterviewGuestManager: React.FC<InterviewGuestManagerProps> = ({
  value = [],
  onChange,
}) => {
  const [emailInput, setEmailInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleAddGuest = () => {
    if (!emailInput.trim()) {
      setError('Please enter an email address');
      return;
    }

    // Support comma-separated emails
    const emails = emailInput
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);

    // Validate all emails
    const invalidEmails = emails.filter((email) => !EMAIL_REGEX.test(email));
    if (invalidEmails.length > 0) {
      setError(`Invalid email(s): ${invalidEmails.join(', ')}`);
      return;
    }

    // Check for duplicates
    const newValidEmails = emails.filter((email) => !value.includes(email));
    if (newValidEmails.length === 0) {
      setError('All entered emails are already added');
      return;
    }

    // Check maximum limit
    if (value.length + newValidEmails.length > MAX_GUESTS) {
      setError(`Maximum ${MAX_GUESTS} guests allowed`);
      return;
    }

    // Add valid emails
    const updatedGuests = [...value, ...newValidEmails];
    onChange?.(updatedGuests);
    setEmailInput('');
    setError(null);
    message.success(`${newValidEmails.length} guest(s) added`);
  };

  const handleRemoveGuest = (email: string) => {
    const updatedGuests = value.filter((g) => g !== email);
    onChange?.(updatedGuests);
    message.success('Guest removed');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailInput(e.target.value);
    setError(null);
  };

  const handleInputBlur = () => {
    if (emailInput.trim() && !EMAIL_REGEX.test(emailInput.trim())) {
      setError('Invalid email format');
    }
  };

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ width: '100%', marginBottom: 16 }}>
        <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 0, fontWeight: 600 }}>
          External Guests <Text type="secondary" style={{ fontSize: 14, fontWeight: 'normal' }}>(Optional)</Text>
        </Typography.Title>
        {value.length > 0 && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {value.length} guest(s) added
          </Text>
        )}
      </Flex>
      
      <Flex gap={8} style={{ marginBottom: 12 }}>
        <Input
          placeholder="Enter guest email(s), separated by commas"
          value={emailInput}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onPressEnter={handleAddGuest}
          style={{ flex: 1 }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddGuest}
          disabled={!emailInput.trim() || value.length >= MAX_GUESTS}
        >
          Add
        </Button>
      </Flex>

      {error && (
        <Text type="danger" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          {error}
        </Text>
      )}

      {value.length > 0 && (
        <List
          size="small"
          dataSource={value}
          renderItem={(email) => (
            <List.Item
              actions={[
                <Button
                  key="remove"
                  type="link"
                  danger
                  icon={<MinusCircleOutlined />}
                  onClick={() => handleRemoveGuest(email)}
                  style={{ padding: 0 }}
                >
                  Remove
                </Button>
              ]}
            >
              <Text>{email}</Text>
            </List.Item>
          )}
        />
      )}

      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
        Maximum {MAX_GUESTS} guests allowed. Enter multiple emails separated by commas.
      </Text>
    </div>
  );
};

export default InterviewGuestManager;
